import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { PerformanceLog } from '../types';
import { ChartContainer, ChartTooltipContent } from '@/components/ui/chart';

interface ResponseTimeChartProps {
  data: PerformanceLog[];
  isLoading: boolean;
  height?: number;
  simplified?: boolean;
}

export const ResponseTimeChart: React.FC<ResponseTimeChartProps> = ({ 
  data, 
  isLoading, 
  height = 400,
  simplified = false
}) => {
  if (isLoading) {
    return <Skeleton className="w-full" style={{ height }} />;
  }

  // Sanitize data to ensure valid values
  const sanitizedData = data.filter(item => 
    item && 
    typeof item.avg_total_time === 'number' && 
    !isNaN(item.avg_total_time) &&
    typeof item.avg_processing_time === 'number' && 
    !isNaN(item.avg_processing_time)
  );
  
  // Group data by operation type
  const operationTypes = Array.from(new Set(sanitizedData.map(item => item.operation_type)));

  // Format dates to be more readable
  const formattedData = sanitizedData.map(item => ({
    ...item,
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    formattedOperationType: item.operation_type.replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
    // Ensure values are within reasonable ranges for display
    avg_total_time: Math.min(Math.max(0, item.avg_total_time), 30000), // Cap at 30 seconds
    avg_processing_time: Math.min(Math.max(0, item.avg_processing_time), 30000), // Cap at 30 seconds
    cache_hit_rate: Math.min(Math.max(0, item.cache_hit_rate || 0), 100) // 0-100%
  }));

  // For simplified view, group by date and average across operation types
  let chartData = formattedData;
  if (simplified) {
    const dateGroups: Record<string, any> = formattedData.reduce((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = {
          date: item.date,
          avg_total_time: 0,
          avg_processing_time: 0,
          count: 0,
          cache_hit_rate: 0
        };
      }
      acc[item.date].avg_total_time += item.avg_total_time * item.count;
      acc[item.date].avg_processing_time += item.avg_processing_time * item.count;
      acc[item.date].count += item.count;
      acc[item.date].cache_hit_rate += item.cache_hit_rate * item.count;
      return acc;
    }, {} as Record<string, any>);

    chartData = Object.values(dateGroups).map(group => ({
      ...group,
      avg_total_time: group.count > 0 ? group.avg_total_time / group.count : 0,
      avg_processing_time: group.count > 0 ? group.avg_processing_time / group.count : 0,
      cache_hit_rate: group.count > 0 ? group.cache_hit_rate / group.count : 0
    })).sort((a, b) => a.date.localeCompare(b.date));
  }

  // Return empty state if no valid data
  if (chartData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400">
        <p>No performance data available for the selected period</p>
      </div>
    );
  }

  const chartConfig = {
    processing_time: {
      label: "Processing Time",
      color: "#0ac389" // Emerald green
    },
    total_time: {
      label: "Total Time",
      color: "#9b87f5" // Royal purple
    },
    cache_rate: {
      label: "Cache Hit Rate",
      color: "#F97316" // Coral orange
    }
  };

  return (
    <ChartContainer config={chartConfig} className="w-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#2d2d2d" />
          <XAxis 
            dataKey="date" 
            tick={{ fill: '#9CA3AF' }} 
            axisLine={{ stroke: '#2d2d2d' }}
            tickLine={{ stroke: '#2d2d2d' }}
          />
          <YAxis 
            yAxisId="time"
            tick={{ fill: '#9CA3AF' }}
            axisLine={{ stroke: '#2d2d2d' }}
            tickLine={{ stroke: '#2d2d2d' }}
            label={{ 
              value: 'Time (ms)', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: '#9CA3AF' }
            }}
          />
          {!simplified && (
            <YAxis 
              yAxisId="rate"
              orientation="right"
              tick={{ fill: '#9CA3AF' }}
              axisLine={{ stroke: '#2d2d2d' }}
              tickLine={{ stroke: '#2d2d2d' }}
              label={{ 
                value: 'Cache Hit Rate (%)', 
                angle: 90, 
                position: 'insideRight',
                style: { fill: '#9CA3AF' }
              }}
            />
          )}
          <Tooltip content={<ChartTooltipContent />} />
          <Legend 
            verticalAlign="top" 
            wrapperStyle={{ paddingBottom: 10 }}
          />
          
          {simplified ? (
            <>
              <Line
                type="monotone"
                dataKey="avg_total_time"
                name="Total Time"
                stroke="var(--color-total_time)"
                activeDot={{ r: 8 }}
                yAxisId="time"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="avg_processing_time"
                name="Processing Time"
                stroke="var(--color-processing_time)"
                yAxisId="time"
                strokeWidth={2}
              />
            </>
          ) : (
            <>
              {operationTypes.map((type, index) => (
                <Line
                  key={type}
                  type="monotone"
                  dataKey="avg_total_time"
                  name={`${type.replace(/_/g, ' ')} Total Time`}
                  stroke={`hsl(${index * 60}, 70%, 60%)`}
                  activeDot={{ r: 8 }}
                  yAxisId="time"
                  strokeWidth={2}
                  connectNulls
                  data={formattedData.filter(item => item.operation_type === type)}
                />
              ))}
              <Line
                type="monotone"
                dataKey="cache_hit_rate"
                name="Cache Hit Rate"
                stroke="var(--color-cache_rate)"
                yAxisId="rate"
                strokeDasharray="3 3"
                strokeWidth={2}
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};
